import Axios from 'axios'

import store from '@/redux/store'
import appFetch from '@/utils/appFetch'
import setHeaderAccessToken from './setHeaderAccessToken'

export const WIDGET_SESSION_ERROR_CODE = 'WEB_API_WIDGET_SESSION_INVALID'

// ---------------------------------------------------------------------------
// Fetch adapter — replaces Axios's default XHR adapter in widget mode.
//
// Why: XMLHttpRequest (Axios 0.x's only built-in adapter) cannot suppress
// same-origin cookies — withCredentials:false only prevents cross-origin
// credential sharing. The Fetch API's credentials:'omit' is the only
// browser-level mechanism that removes cookies from ALL requests, including
// same-origin ones.
//
// Axios 0.x calls config.adapter(config) and expects a settled Promise that
// resolves with an Axios-shaped response object or rejects with an
// Axios-shaped error object. dispatchRequest.js already flattens and
// transforms headers/data before the adapter is invoked, so we receive clean
// values and don't need to repeat that logic here.
// ---------------------------------------------------------------------------
const noCredentialsAdapter = async (config) => {
	const method = (config.method || 'GET').toUpperCase()
	const hasBody = method !== 'GET' && method !== 'HEAD'

	// config.headers is a flat object by the time the adapter runs —
	// Axios's dispatchRequest removes nested method-keys (common, post, …).
	const headers = {}
	Object.entries(config.headers || {}).forEach(([key, val]) => {
		if (val !== null && val !== undefined) headers[key] = String(val)
	})

	// config.data has already been processed by transformRequest (JSON-stringified
	// for object payloads), so pass it through as-is.
	const abortController = new AbortController()

	// Honour Axios cancel tokens (0.x API)
	if (config.cancelToken) {
		config.cancelToken.promise.then(() => abortController.abort())
	}
	// Honour AbortSignal passed directly (Axios 0.22+)
	if (config.signal) {
		config.signal.addEventListener('abort', () => abortController.abort(), { once: true })
	}

	let fetchResponse
	try {
		fetchResponse = await appFetch(config.url, {
			method,
			headers,
			body: hasBody && config.data != null ? config.data : undefined,
			credentials: 'omit', // never send cookies, not even for same-origin
			signal: abortController.signal,
		})
	} catch (fetchErr) {
		if (fetchErr.name === 'AbortError') {
			const cancelErr = new Error('canceled')
			cancelErr.code = 'ERR_CANCELED'
			cancelErr.name = 'CanceledError'
			cancelErr.config = config
			throw cancelErr
		}
		const netErr = new Error('Network Error')
		netErr.config = config
		throw netErr
	}

	// Collect response headers as a plain object (same shape as Axios XHR adapter)
	const responseHeaders = {}
	fetchResponse.headers.forEach((value, key) => {
		responseHeaders[key] = value
	})

	let responseData
	if (config.responseType === 'blob') {
		responseData = await fetchResponse.blob()
	} else {
		// Parse body — try JSON, fall back to raw text
		const rawText = await fetchResponse.text().catch(() => '')
		try {
			responseData = rawText ? JSON.parse(rawText) : rawText
		} catch {
			responseData = rawText
		}
	}

	const axiosResponse = {
		data: responseData,
		status: fetchResponse.status,
		statusText: fetchResponse.statusText,
		headers: responseHeaders,
		config,
		request: null,
	}

	if (fetchResponse.status >= 200 && fetchResponse.status < 300) {
		return axiosResponse
	}

	// Reject with an Axios-compatible error so handleError.jsx receives the
	// same error shape as with the XHR adapter.
	const httpErr = new Error(`Request failed with status code ${fetchResponse.status}`)
	httpErr.config = config
	httpErr.request = null
	httpErr.response = axiosResponse
	httpErr.isAxiosError = true
	httpErr.toJSON = function () {
		return { message: this.message, name: this.name, code: this.code, status: fetchResponse.status }
	}
	throw httpErr
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Installs a permanent Axios request interceptor that:
 *   1. Replaces the XHR adapter with a Fetch adapter using credentials:'omit'
 *      so that cookies are never sent, including for same-origin requests.
 *   2. Also sets withCredentials:false as belt-and-suspenders for any path
 *      that bypasses the adapter swap.
 *
 * Must be called once, before the first Axios request is made.
 */
export const installWidgetAxiosInterceptor = () => {
	Axios.interceptors.request.use((config) => {
		config.withCredentials = false
		config.adapter = noCredentialsAdapter
		return config
	})
}

/**
 * Reads the widget session_token stored in Redux embedMetaData and sets it
 * as the global Axios Authorization header. Idempotent — safe to call on
 * every user action; no-ops if no token is present.
 *
 * Must be called before the first post-load API request in widget mode.
 */
export const applyWidgetSessionToken = () => {
	const { embedMetaData } = store.getState().theme
	const sessionToken = embedMetaData?.session_token
	if (sessionToken) setHeaderAccessToken(sessionToken)
}
